import Clear from "@mui/icons-material/Clear";
import FilterList from "@mui/icons-material/FilterList";
import Tune from "@mui/icons-material/Tune";
import {
  Box,
  Button,
  IconButton,
  Stack,
  SxProps,
  TextField,
  Tooltip,
} from "@mui/material";
import { extractKeyValuePairs } from "encamp-omnisearch/src/utils/util";
import { useCurrentUser } from "hooks/useCurrentUser";
import { FilterKey } from "hooks/useOmnisearchDatagrid";
import React, { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocalStorageState } from "../../hooks/useLocalStorageState";
import { metricsLogger } from "../../utils/metricsLogger";
import { FilterGroup, FilterPopover } from "./FilterPopover";

export type Filter = {
  omnisearch?: string;
};

export type OmnisearchProps = {
  autoFocus?: boolean;
  columnFilterKeys?: FilterKey[];
  sx?: SxProps;
  omnisearch: string;
  setOmnisearch: (newVal: string) => void;
  /** Immediate update (no debounce) - for clear button, programmatic changes */
  setOmnisearchImmediate?: (newVal: string) => void;
  onApplyFilters?: (newVal: string) => void;
  inputRef?: React.RefObject<HTMLElement | null>;
  resetRef?: React.RefObject<HTMLButtonElement | null>;
  filterGroups?: FilterGroup[];
  /** Show filter button inside the search input (tune icon). Default true. */
  showFilterButtonInside?: boolean;
  /** Show external "Filters" button outside the search input. Default false. */
  showExternalFilterButton?: boolean;
  /** 
   * Custom render function for the external filter button.
   * Receives the click handler and returns the button element.
   * If not provided, a default CommandButton-style button is rendered.
   */
  renderFilterButton?: (onClick: (event: MouseEvent<HTMLButtonElement>) => void) => React.ReactNode;
  persistenceKey?: string;
  keyAliases?: Record<string, string>;
  /** Optional tenantId for backend queries. */
  tenantId?: string;
  /** Opt-in for wide (3-column) filter layout. */
  wideLayout?: boolean;
};

export function Omnisearch({
  autoFocus = false,
  columnFilterKeys,
  sx = {},
  omnisearch,
  setOmnisearch,
  setOmnisearchImmediate,
  onApplyFilters,
  inputRef,
  resetRef,
  filterGroups,
  showFilterButtonInside = true,
  showExternalFilterButton = false,
  renderFilterButton,
  persistenceKey,
  keyAliases = {},
  tenantId,
  wideLayout,
}: OmnisearchProps) {
  const currentUser = useCurrentUser();
  
  // Filter popover anchor - managed internally to avoid parent re-renders
  const [filterButtonAnchor, setFilterButtonAnchor] =
    useState<HTMLButtonElement | null>(null);

  // Local input state for responsive TextField - prevents parent re-renders from blocking typing
  const [localInputValue, setLocalInputValue] = useState(omnisearch);
  const isLocalChange = useRef(false);

  // Sync local state when parent's omnisearch changes (e.g., from URL updates, filter applies)
  // Only sync if it wasn't a local change (user typing) to avoid overwriting mid-edit
  useEffect(() => {
    if (omnisearch !== localInputValue) {
      if (!isLocalChange.current) {
        setLocalInputValue(omnisearch);
      }
    }
    // Reset the flag after processing the omnisearch change
    isLocalChange.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [omnisearch]); // Intentionally omit localInputValue - we only care when omnisearch prop changes

  // Handle input changes - update local state immediately, notify parent
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      isLocalChange.current = true;
      setLocalInputValue(newValue);
      setOmnisearch(newValue);
    },
    [setOmnisearch]
  );

  // Handle clear - update both local and parent (immediate, no debounce)
  const handleClear = useCallback(() => {
    metricsLogger.startInteraction('omnisearch.clear');
    isLocalChange.current = true;
    setLocalInputValue("");
    if (setOmnisearchImmediate) {
      setOmnisearchImmediate("");
    } else {
      setOmnisearch("");
    }
  }, [setOmnisearch, setOmnisearchImmediate]);

  // Handler for opening filter popover - used by both internal and external buttons
  const handleOpenFilter = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      setFilterButtonAnchor(event.currentTarget);
    },
    []
  );

  const handleCloseFilter = useCallback(() => {
    setFilterButtonAnchor(null);
  }, []);

  const onApply = useCallback(
    (newOmnisearch: string) => {
      if (onApplyFilters) {
        onApplyFilters(newOmnisearch);
      } else {
        setOmnisearch(newOmnisearch);
      }
    },
    [onApplyFilters, setOmnisearch]
  );

  const isFilterOpen = useMemo(
    () => Boolean(filterButtonAnchor),
    [filterButtonAnchor]
  );

  // Helper function to filter out unknown filter keys from an omnisearch string
  const filterValidKeys = useCallback(
    (omnisearchString: string): string => {
      if (!columnFilterKeys || !omnisearchString.trim()) {
        return omnisearchString;
      }

      const validKeys = new Set(columnFilterKeys.map((key) => key.key));
      const pairs = extractKeyValuePairs(omnisearchString);

      const filteredPairs = pairs.filter((pair) => {
        const isValid =
          validKeys.has(pair.key) ||
          (keyAliases[pair.key] && validKeys.has(keyAliases[pair.key]));
        return isValid;
      });

      let filteredOmnisearch = omnisearchString;
      pairs.forEach((pair) => {
        const originalPairText = omnisearchString.substring(pair.start, pair.end);
        filteredOmnisearch = filteredOmnisearch.replace(originalPairText, "");
      });
      filteredOmnisearch = filteredOmnisearch.replace(/\s+/g, " ").trim();

      const validPairStrings = filteredPairs.map((pair) => {
        const values = pair.value.map((val) => {
          const needsQuotes =
            val.includes(" ") ||
            val.includes(":") ||
            val.includes('"') ||
            val.includes("|") ||
            val.includes(",");
          if (needsQuotes) {
            const escapedValue = val.replace(/"/g, '""');
            return `"${escapedValue}"`;
          }
          return val;
        });
        const valueString = values.length > 1 ? values.join("|") : values[0];
        return `${pair.key}:${valueString}`;
      });

      const result = [filteredOmnisearch, ...validPairStrings]
        .filter((part) => part.trim().length > 0)
        .join(" ")
        .trim();

      return result;
    },
    [columnFilterKeys, keyAliases]
  );

  // Data grid specific saved filters
  const savedFiltersKey = persistenceKey
    ? `saved-filters-${persistenceKey}|${currentUser.user?.email}`
    : `saved-filters|${currentUser.user?.email}`;

  const [savedFilters, setSavedFilters] = useLocalStorageState<{
    [name: string]: string;
  }>(savedFiltersKey, { defaultValue: {} });

  const [legacyFavorites] = useLocalStorageState<{
    [favorite: string]: string;
  }>(`filter-favorites|${currentUser.user?.email}`, { defaultValue: {} });

  const savedFiltersList = useMemo(() => {
    const currentFilters = Object.entries(savedFilters).map(([name, query]) => {
      const filteredQuery = filterValidKeys(query);
      return {
        name,
        query: filteredQuery,
        source: "current" as const,
      };
    });

    const legacyFavoritesFiltered = Object.entries(legacyFavorites)
      .map(([name, query]) => {
        const filteredQuery = filterValidKeys(query);
        return {
          name: `⭐ ${name}`,
          query: filteredQuery,
          source: "legacy-favorites" as const,
        };
      })
      .filter((item) => {
        const hasValidKeyValuePairs = item.query.includes(":");
        return hasValidKeyValuePairs;
      });

    return [...currentFilters, ...legacyFavoritesFiltered];
  }, [savedFilters, legacyFavorites, filterValidKeys]);

  const handleSavePreset = useCallback(
    (name: string, query: string) => {
      setSavedFilters((prev) => ({
        ...prev,
        [name]: query,
      }));
    },
    [setSavedFilters]
  );

  const handleDeletePreset = useCallback(
    (name: string) => {
      if (name.startsWith("⭐ ")) {
        return;
      }
      setSavedFilters((prev) => {
        const { [name]: _, ...rest } = prev;
        return rest;
      });
    },
    [setSavedFilters]
  );

  // Default external filter button
  const defaultFilterButton = (
    <Button
      variant="outlined"
      size="small"
      onClick={handleOpenFilter}
      startIcon={<FilterList fontSize="small" />}
      sx={{ 
        whiteSpace: "nowrap",
        minWidth: "auto",
      }}
    >
      Filters
    </Button>
  );

  // Determine if we should show any external filter button
  const shouldShowExternalButton = 
    showExternalFilterButton && 
    columnFilterKeys && 
    columnFilterKeys.length > 0;

  return (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{
        width: {
          sm: "80%",
          xs: "100%",
        },
        alignItems: "center",
        ...sx,
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          position: "relative",
          display: "flex",
          alignItems: "center",
        }}
      >
        <TextField
          autoFocus={autoFocus}
          inputRef={inputRef}
          sx={{ flexGrow: 1 }}
          name="search"
          value={localInputValue ?? ""}
          onChange={handleInputChange}
          label="Search"
          InputProps={{
            endAdornment: (
              <Box sx={{ display: "flex" }}>
                <IconButton
                  ref={resetRef}
                  sx={{
                    display: localInputValue?.length ? "default" : "none",
                  }}
                  onClick={handleClear}
                  aria-label="clear input"
                >
                  <Clear fontSize="small" />
                </IconButton>
                {columnFilterKeys && showFilterButtonInside && (
                  <Tooltip title="Filters">
                    <IconButton
                      onClick={handleOpenFilter}
                      aria-label="show filters"
                    >
                      <Tune fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            ),
          }}
        />
      </Box>

      {/* External filter button - rendered inside Omnisearch to keep state colocated */}
      {shouldShowExternalButton && (
        renderFilterButton 
          ? renderFilterButton(handleOpenFilter)
          : defaultFilterButton
      )}

      {/* Only render FilterPopover when open to prevent re-renders during typing */}
      {columnFilterKeys && isFilterOpen && (
        <FilterPopover
          isOpen={isFilterOpen}
          filterButtonAnchor={filterButtonAnchor}
          handleCloseFilter={handleCloseFilter}
          columnFilterKeys={columnFilterKeys}
          onApply={onApply}
          omnisearch={omnisearch}
          savedFilters={savedFiltersList}
          onSavePreset={handleSavePreset}
          onDeletePreset={handleDeletePreset}
          filterGroups={filterGroups}
          keyAliases={keyAliases}
          tenantId={tenantId}
          wideLayout={wideLayout}
        />
      )}
    </Stack>
  );
}
